import { LoaderFullPage } from '@cart-ui/LoaderFullPage/LoaderFullPage'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type ECommercePaymentExecution, type UserPaymentTokenInfo } from '@hooks-api/types/dataModel/eCommercePayments'
import { type CardholderInfo, PaymentMethodType, PaymentServiceType, type PaymentMethod } from '@hooks-api/types/dataModel/payment'
import { CARD_SOURCE, type CreditCardUpdateInfo } from '@payments'
import { CheckoutPayCreditCardModule } from '@payments/lib/checkout-pay/CheckoutPayCreditCardModule'
import { getActivePaymentModuleB2B } from '@payments/lib/getActivePaymentModule'
import { TappayCreditCardModule } from '@payments/lib/TappayCreditCardModule'
import { Backdrop } from '@pets-ui/Backdrop/Backdrop'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { zIndexValues } from '@pets-ui/tokens/zIndex.stylex'
import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import { create } from 'zustand'
import { type CardValidation } from '../../utils/utility-process-credit-card-pay-module'
import { type AppliedPaymentModule } from '../../utils/utility-process-pay-module'

interface PaymentContextProps {
  paymentMethods: PaymentMethod[]
  selectedPayment?: PaymentMethod
  setSelectedPayment: (payment: PaymentMethod) => void
  getActivePaymentModuleB2B: typeof getActivePaymentModuleB2B
  paymentExecution?: ECommercePaymentExecution
  setPaymentExecution: (paymentExecution: ECommercePaymentExecution) => void
  configuredPaymentModule: AppliedPaymentModule | undefined
  setConfiguredPaymentModule: (paymentModule: AppliedPaymentModule | undefined) => void
  setCardValidation: (value: CardValidation) => void
  cardValidation: CardValidation
  isSdkInitialized: boolean
  resetCreditCardModule: () => void
  setUserPaymentTokenInfo: (info: UserPaymentTokenInfo | undefined) => void
  userPaymentTokenInfo: UserPaymentTokenInfo | undefined
  cardSource: string
  setCardSource: (cardSource: string) => void
  setIsLoadingPayment: (isLoadingPayment: boolean) => void
  isLoadingPayment: boolean
  isCurrentCreditCardRemembered: boolean
  setIsCurrentCreditCardRemembered: (value: boolean) => void
  isProcessingCheckout: boolean
  setIsProcessingCheckout: (value: boolean) => void
  isExecutingPayment: boolean
  setIsExecutingPayment: (value: boolean) => void
  /** Save the credit card info validated by Tappay */
  creditCardUpdateInfo: CreditCardUpdateInfo
  setCreditCardUpdateInfo: (cardUpdateInfo: CreditCardUpdateInfo) => void
  /** Save the credit card holder info by us */
  creditCardholderInfo: CardholderInfo | null
  setCreditCardholderInfo: (cardholderInfo: CardholderInfo | null) => void
  /** Save payment execution errors caused by wrong cardholder info */
  cardholderInfoError: string
  setCardholderInfoError: (error: string) => void
  orderTotalAmount?: number
}
export const PaymentContext = createContext<PaymentContextProps | undefined>(undefined)

/**
 * SDK 整個 app 只會初始化一次，所以使用 store 來判斷是否已經初始化
*/
const useSdkStore = create<{
  isSdkInitialized: boolean
  isSdkInitializing: boolean
  setIsSdkInitialized: (isSdkInitialized: boolean) => void
  setIsSdkInitializing: (isSdkInitializing: boolean) => void
}>()(set => ({
  isSdkInitialized: false,
  isSdkInitializing: false,
  setIsSdkInitialized: (isSdkInitialized: boolean) => set({ isSdkInitialized }),
  setIsSdkInitializing: (isSdkInitializing: boolean) => set({ isSdkInitializing }),
}))

export interface PaymentProviderProps {
  paymentMethods?: PaymentMethod[]
  defaultPaymentMethod?: PaymentMethod
  orderTotalAmount?: number
}

export const PaymentProvider = (props: PropsWithChildren<PaymentProviderProps>) => {
  const { children, paymentMethods = [], defaultPaymentMethod, orderTotalAmount } = props
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod | undefined>(defaultPaymentMethod)
  const [paymentExecution, setPaymentExecution] = useState<ECommercePaymentExecution>()
  const [configuredPaymentModule, setConfiguredPaymentModule] = useState<AppliedPaymentModule | undefined>()
  const [cardSource, setCardSource] = useState(CARD_SOURCE.ADD_CARD)
  const [isLoadingPayment, setIsLoadingPayment] = useState(false)
  const [isCurrentCreditCardRemembered, setIsCurrentCreditCardRemembered] = useState(false)
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false)
  const [isExecutingPayment, setIsExecutingPayment] = useState(false)
  const [cardValidation, setCardValidation] = useState<CardValidation>({
    tappay: null,
    checkoutpay: null,
  })
  const [userPaymentTokenInfo, setUserPaymentTokenInfo] = useState<UserPaymentTokenInfo | undefined>()
  useEffect(() => {
    if (selectedPayment) return
    setSelectedPayment(defaultPaymentMethod)
  }, [defaultPaymentMethod, selectedPayment])

  const isSdkInitialized = useSdkStore(state => state.isSdkInitialized)
  const isSdkInitializing = useSdkStore(state => state.isSdkInitializing)
  const setIsSdkInitializing = useSdkStore(state => state.setIsSdkInitializing)
  const setIsSdkInitialized = useSdkStore(state => state.setIsSdkInitialized)

  /**
   * 回上一頁時，使用者須重設帶ui的payment，如credit card payment，避免回到結帳頁後，第三方無法畫出credit card input fields
   */
  const resetCreditCardModule = useCallbackEvent(() => {
    const tappayModule = getActivePaymentModuleB2B(PaymentServiceType.TAPPAY, PaymentMethodType.CREDIT_CARD_ONCE)
    const checkoutpayModule = getActivePaymentModuleB2B(PaymentServiceType.CHECKOUTPAY, PaymentMethodType.CREDIT_CARD_ONCE)

    if (tappayModule instanceof TappayCreditCardModule) tappayModule.reset()
    if (checkoutpayModule instanceof CheckoutPayCreditCardModule) {
      checkoutpayModule.reset()
    }
  })
  const [cardholderInfoError, setCardholderInfoError] = useState('')
  const [creditCardUpdateInfo, setCreditCardUpdateInfo] = useState<CreditCardUpdateInfo>(null)
  const [creditCardholderInfo, setCreditCardholderInfo] = useState<CardholderInfo | null>(null)

  /**
   * 載入第三方付款模組
   */
  useEffect(() => {
    if (!paymentMethods || paymentMethods.length === 0 || isSdkInitialized || isSdkInitializing) return
    setIsSdkInitializing(true)
    const initPaymentsSdk = async () => {
      try {
        await Promise.all(
          paymentMethods?.map(async (payment) => {
            if (!payment.service_type || !payment.type) {
              return
            }
            const paymentModule = getActivePaymentModuleB2B(payment.service_type, payment.type)
            if (!paymentModule) return
            await paymentModule.loadScript()
          }),
        )
        setIsSdkInitialized(true)
      }
      catch (e) {
        console.error(e)
      }
    }

    initPaymentsSdk().finally(() => {
      setIsSdkInitializing(false)
    })
  }, [isSdkInitialized, isSdkInitializing, paymentMethods, setIsSdkInitialized, setIsSdkInitializing])

  const contextValue = useMemo(() => ({
    setIsLoadingPayment,
    isLoadingPayment,
    getActivePaymentModuleB2B,
    paymentMethods,
    selectedPayment,
    setSelectedPayment,
    paymentExecution,
    setPaymentExecution,
    configuredPaymentModule,
    setConfiguredPaymentModule,
    cardValidation,
    setCardValidation,
    isSdkInitialized,
    resetCreditCardModule,
    userPaymentTokenInfo,
    setUserPaymentTokenInfo,
    cardSource,
    setCardSource,
    isCurrentCreditCardRemembered,
    setIsCurrentCreditCardRemembered,
    isProcessingCheckout,
    setIsProcessingCheckout,
    isExecutingPayment,
    setIsExecutingPayment,
    creditCardholderInfo,
    setCreditCardholderInfo,
    cardholderInfoError,
    setCardholderInfoError,
    creditCardUpdateInfo,
    setCreditCardUpdateInfo,
    orderTotalAmount,
  }), [cardSource, cardValidation, cardholderInfoError, configuredPaymentModule, creditCardUpdateInfo, creditCardholderInfo, isCurrentCreditCardRemembered, isExecutingPayment, isLoadingPayment, isProcessingCheckout, isSdkInitialized, orderTotalAmount, paymentExecution, paymentMethods, resetCreditCardModule, selectedPayment, userPaymentTokenInfo])

  return (
    <>
      {isSdkInitializing && <LoaderFullPage />}
      {isExecutingPayment && (
        <Backdrop open zIndex={zIndexValues.snackbar}>
          <CircularProgress fontSize={36} color={colors.white} />
        </Backdrop>
      )}
      <PaymentContext.Provider value={contextValue}>
        {children}
      </PaymentContext.Provider>
    </>
  )
}

export const useContextPayment = () => {
  const context = useContext(PaymentContext)

  if (!context) {
    throw new Error('useContextPayment must be used within a PaymentProvider')
  }

  return context
}
